import React from 'react';
import { CheckCircle2, Shield, Clock, Zap, Smartphone } from 'lucide-react';
import { PhoneMockup } from './PhoneMockup';
import { Button } from './Button';
import { useNavigate } from 'react-router-dom';

export const DownloadApp: React.FC = () => {
  const navigate = useNavigate();

  const handleGetApp = () => {
    navigate('/waiting-list');
  };

  return (
    <section id="download" className="py-24 bg-secondary overflow-hidden relative">
      {/* Background gradients */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
          
          {/* Content */}
          <div className="flex-1 space-y-10 text-center lg:text-left">
             <div className="space-y-6">
                 <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-bold uppercase tracking-wider backdrop-blur-sm border border-white/10">
                    <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
                    App Benefits
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
                    Experience Healthcare <br/>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Without Boundaries</span>
                </h2>
                <p className="text-lg text-gray-300 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                    The MHC Africa app empowers you with tools to manage your health proactively. Enjoy exclusive benefits designed to save you time and keep you healthy.
                </p>
             </div>

             {/* Benefits Grid */}
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8 pt-4 text-left">
                {[
                    {
                        title: "Skip the Queues",
                        desc: "Book instantly and see a doctor within minutes, not hours.",
                        icon: Clock
                    },
                    {
                        title: "Secure & Private",
                        desc: "Your medical data is encrypted and visible only to you.",
                        icon: Shield
                    },
                    {
                        title: "Real-time Updates",
                        desc: "Get instant notifications for results and appointment reminders.",
                        icon: Zap
                    },
                    {
                        title: "All-in-One Access",
                        desc: "Prescriptions, reports, and payments in one single dashboard.",
                        icon: CheckCircle2
                    }
                ].map((item, i) => (
                    <div key={i} className="flex gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-blue-400 flex-shrink-0 border border-white/5">
                            <item.icon size={24} />
                        </div>
                        <div>
                            <h4 className="text-white font-bold text-lg mb-1">{item.title}</h4>
                            <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
                        </div>
                    </div>
                ))}
             </div>

             <div className="pt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start border-t border-white/10">
                 <Button 
                  onClick={handleGetApp}
                  className="px-10 py-5 text-xl gap-3 shadow-2xl shadow-primary/20 min-w-[260px]"
                 >
                    <Smartphone size={24} strokeWidth={2.5} />
                    <span className="font-bold">Get the App</span>
                 </Button>
             </div>
          </div>

          {/* Visual */}
          <div className="flex-1 w-full relative perspective-1000 flex justify-center lg:justify-end">
             <div className="relative">
                 {/* Glow effect behind phone */}
                 <div className="absolute inset-0 bg-blue-500/30 blur-[60px] rounded-full scale-110"></div>
                 
                 <PhoneMockup 
                    className="relative z-10 rotate-[-5deg] hover:rotate-0 transition-transform duration-700"
                    imageSrc="https://2bd20b7db6dd5db900dc830b78e06be9.cdn.bubble.io/f1765371884637x712260310363814800/Screenshot_20251210_144354_BubbleGo.jpg?_gl=1*18i571r*_gcl_au*OTYxMjAzODg4LjE3NjUzMTUwMDg.*_ga*Njg0NDczNjkxLjE3NjUzMTUwMDg.*_ga_BFPVR2DEE2*czE3NjUzMTUwMDgkbzEkZzEkdDE3NjUzNzE4NDYkajU0JGwwJGgw"
                 />

                 {/* Floating Elements */}
                 <div className="absolute top-1/4 -left-12 bg-white p-4 rounded-2xl shadow-xl flex items-center gap-4 animate-float z-20 max-w-[240px]">
                     <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                         <Shield size={20} />
                     </div>
                     <div>
                         <p className="text-xs font-bold text-gray-500">Data Privacy</p>
                         <p className="text-sm font-bold text-secondary">100% Encrypted</p>
                     </div>
                 </div>

                  <div className="absolute bottom-1/4 -right-8 bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-2xl shadow-xl animate-float-delayed z-20 text-white max-w-[200px]">
                     <p className="text-xs font-medium text-blue-200 mb-2">Instant Access</p>
                     <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                            <Zap size={16} className="fill-white text-white" />
                        </div>
                        <div>
                            <p className="text-xs font-bold">Lab Results</p>
                            <p className="text-[10px] text-gray-300">Ready in 10 mins</p>
                        </div>
                     </div>
                 </div>
             </div>
          </div>

        </div>
      </div>
    </section>
  );
};